import { useDraggable } from '@dnd-kit/core';
import { useEffect, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { SEGMENTS_COLOR_LADDER } from '@/app/constants';
import { useDevice } from '@/app/hooks';
import type { SegmentType } from '@/app/types';
import { cn } from '@/app/utils';

interface DraggableSegmentProps {
  segment: SegmentType;
  scaleWidth: number;
  rowHeight: number;
  index: number;
  onResizeStart: (id: string, direction: 'left' | 'right', e: React.MouseEvent | React.TouchEvent) => void;
  onDelete?: (id: string) => void;
}

export const DraggableSegment: React.FC<DraggableSegmentProps> = ({
  segment,
  scaleWidth,
  rowHeight,
  index,
  onResizeStart,
  onDelete,
}) => {
  const { isMobile } = useDevice();
  const [isSelected, setIsSelected] = useState(false);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: segment.id,
    data: segment,
  });

  const startPosition = segment.startTime * scaleWidth;
  const segmentWidth = (segment.endTime - segment.startTime) * scaleWidth;

  // 根据索引计算垂直位置，每项间隙为4px
  const topPosition = index * (rowHeight + 4);

  const style = transform
    ? {
        left: `${startPosition}px`,
        width: `${segmentWidth}px`,
        top: `${topPosition}px`,
        transform: `translate3d(${transform.x}px, 0px, 0)`,
      }
    : {
        left: `${startPosition}px`,
        width: `${segmentWidth}px`,
        top: `${topPosition}px`,
      };

  // 处理键盘删除事件
  const handleKeyDown = (e: KeyboardEvent) => {
    if (isSelected && (e.key === 'Backspace' || e.key === 'Delete')) {
      onDelete?.(segment.id);
      setIsSelected(false);
    }
  };

  useEffect(() => {
    if (!isMobile && isSelected) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, isSelected]);

  // 点击其他地方取消选中
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const segmentElement = document.getElementById(`segment-${segment.id}`);

      if (segmentElement && !segmentElement.contains(target)) {
        setIsSelected(false);
      }
    };

    if (isSelected) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [isSelected, segment.id]);

  return (
    <div
      id={`segment-${segment.id}`}
      ref={setNodeRef}
      className={cn(
        'group absolute z-10 flex cursor-move items-center rounded-10 px-[10px] backdrop-blur-[2px] transition-all duration-200 after:pointer-events-none after:absolute after:inset-0 after:hidden after:rounded-10 after:border after:border-white/30 after:content-[""] hover:after:block',
        `h-[${rowHeight}px]`,
        isDragging ? 'opacity-60' : '',
        isSelected ? 'after:block' : '',
        isMobile && 'pr-[30px]',
      )}
      style={{
        ...style,
        backgroundColor: `${segment.color || SEGMENTS_COLOR_LADDER[0]}a3`,
      }}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        setIsSelected(true);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        setIsSelected(true);
        listeners?.onTouchStart?.(e);
      }}
    >
      {/* 左侧拉伸手柄 */}
      <div
        className={cn(
          '-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-0 z-10 h-[26px] w-3 cursor-ew-resize items-center justify-center rounded-full border border-white/30',
          isMobile && isSelected ? 'flex' : 'hidden group-hover:flex',
        )}
        style={{
          backgroundColor: `${segment.color || SEGMENTS_COLOR_LADDER[0]}a3`,
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onResizeStart(segment.id, 'left', e);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          onResizeStart(segment.id, 'left', e);
        }}
      >
        <BaseLineLeft className="text-[12px] text-white" />
      </div>

      <CustomTooltip content={segment.text} sideOffset={index === 0 ? -10 : 0}>
        <span className="inline-block select-none truncate font-normal text-sm text-white leading-[1.4]">
          {segment.text}
        </span>
      </CustomTooltip>

      {/* 右侧拉伸手柄 */}
      <div
        className={cn(
          '-translate-y-1/2 absolute top-1/2 right-0 z-10 h-[26px] w-3 translate-x-1/2 cursor-ew-resize items-center justify-center rounded-full border border-white/30',
          isMobile && isSelected ? 'flex' : 'hidden group-hover:flex',
        )}
        style={{
          backgroundColor: `${segment.color || SEGMENTS_COLOR_LADDER[0]}a3`,
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onResizeStart(segment.id, 'right', e);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          onResizeStart(segment.id, 'right', e);
        }}
      >
        <BaseLineRight className="text-[12px] text-white" />
      </div>

      {/* 删除按钮，移动端才有 */}
      <div
        className={cn(
          'absolute right-3 hidden cursor-pointer items-center justify-center text-white/60 hover:text-white',
          isMobile && isSelected && 'block',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(segment.id);
        }}
      >
        <BaseLineDelete className="text-base" />
      </div>
    </div>
  );
};
